/* eslint-disable react-hooks/exhaustive-deps */
/**
 * useBodyImages Hook
 *
 * Custom hook for loading body images from IndexedDB.
 * Handles image validation, loading, and state management.
 */
import { useState, useEffect, useCallback } from "react";
import {
  getAllKeysFromDB,
  getFullObjectFromDB,
  BODY_STORE_NAME,
} from "../../../../../IndexedDBUtils";
import { getEventScope, isCurrentScope } from "../../../../../eventIdentity";

const useBodyImages = (eventName) => {
  const [images, setImages] = useState([]);
  const [imagesLoaded, setImagesLoaded] = useState(false);
  // Validates image blobs to ensure they can be displayed
  const checkImageValidity = useCallback(async (blob) => {
    return new Promise((resolve) => {
      if (!blob) {
        resolve(false);
        return;
      }

      try {
        const img = new Image();
        const objectUrl = URL.createObjectURL(blob);

        img.onload = () => {
          URL.revokeObjectURL(objectUrl);
          resolve(true);
        };

        img.onerror = () => {
          URL.revokeObjectURL(objectUrl);
          resolve(false);
        };

        img.src = objectUrl;
      } catch (err) {
        console.error("Error checking image validity:", err);
        resolve(false);
      }
    });
  }, []);

  // Load body images from database
  useEffect(() => {
    // The load belongs to this mounted page and the event visit it opened in:
    // after an unmount or an event switch it stops (no more blob URLs minted,
    // no state published) instead of finishing for a page nobody sees.
    const scope = getEventScope();
    let cancelled = false;
    const isCurrent = () => !cancelled && isCurrentScope(scope);

    const loadImagesFromDB = async () => {
      try {
        const allKeys = await getAllKeysFromDB(eventName, BODY_STORE_NAME);
        if (!isCurrent()) return;

        if (allKeys.length === 0) {
          setImages([]);
          setImagesLoaded(true);
          return;
        }

        let loadedImages = [];

        for (const path of allKeys) {
          try {
            const bodyObject = await getFullObjectFromDB(
              eventName,
              BODY_STORE_NAME,
              path
            );
            if (!isCurrent()) {
              loadedImages.forEach(({ url }) => URL.revokeObjectURL(url));
              return;
            }
            if (!bodyObject || !bodyObject.imageData) continue;

            // Check if face_position_ratio exists, add a fallback if not
            if (!bodyObject.face_position_ratio) {
              console.warn(
                `Body image ${path} missing face_position_ratio data, using fallback`
              );
              bodyObject.face_position_ratio = { x: 100, y: 100 }; // Fallback position
            }

            const blob = bodyObject.imageData;
            const isValid = await checkImageValidity(blob);
            if (!isCurrent()) {
              loadedImages.forEach(({ url }) => URL.revokeObjectURL(url));
              return;
            }
            if (!isValid) continue;

            const objectUrl = URL.createObjectURL(blob);

            const imageObject = {
              path,
              url: objectUrl,
              face_position_ratio: bodyObject.face_position_ratio || {
                x: 0.5,
                y: 0.2,
              },
              width: bodyObject.width || 0,
              height: bodyObject.height || 0,
              faceStyle: null,
            };

            loadedImages.push(imageObject);
          } catch (err) {
            console.error(`Error processing image ${path}:`, err);
          }
        }

        if (!isCurrent()) {
          loadedImages.forEach(({ url }) => URL.revokeObjectURL(url));
          return;
        }
        setImages(loadedImages);
        setImagesLoaded(true);
      } catch (err) {
        console.error("Error loading body images:", err);
        if (isCurrent()) setImagesLoaded(true);
      }
    };

    loadImagesFromDB();
    return () => {
      cancelled = true;
    };
  }, [checkImageValidity]);

  return { images, setImages, imagesLoaded };
};

export default useBodyImages;
